import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { negate } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { DsBannerState } from '@levelaccess/design-system';

import { IAuditStandard } from '../../../../shared/interfaces/audit-standard.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { FormService } from '../../services/form.service';
import { TranslateService } from '../../translate/translate.service';
import {
  EvaluationStandards,
  EVALUATION_STANDARD_TO_AUDIT_STANDARD,
  EvaluationMediums,
  IDENTIFIER_TO_EVALUATION_STANDARDS,
} from '../../../../shared/constants/manual-evaluation';
import { $auditStandard, AuditStandards } from '../../../../shared/constants/audit-standard';
import { standardNameAndLevel } from '../../../../shared/constants/scanning';
import { SharedAuditStandardsUtility } from '../../../../shared/utils/audit-standards.utility';

enum FormFields {
  evaluationStandards = 'evaluationStandards',
  wcagStandard = 'wcagStandard',
}

interface FormType {
  [FormFields.evaluationStandards]: FormControl<EvaluationStandards[]>;
  [FormFields.wcagStandard]: FormControl<standardNameAndLevel>;
}

export interface SelectorConfig {
  mainStandardsChoice: EvaluationStandards[];
  wcagStandardConformanceLevels: EvaluationStandards[];
}

const isWcag: (standard: EvaluationStandards) => boolean = (standard: EvaluationStandards): boolean =>
  EVALUATION_STANDARD_TO_AUDIT_STANDARD[standard] === AuditStandards.wcag;

@Component({
  selector: 'app-standards-selector',
  templateUrl: './standards-selector.component.html',
  standalone: false,
})
export class StandardsSelectorComponent implements OnInit {
  public FormFields: typeof FormFields = FormFields;
  public evaluationStandardsError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public wcagStandardsError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public EVALUATION_STANDARD_TO_AUDIT_STANDARD: typeof EVALUATION_STANDARD_TO_AUDIT_STANDARD =
    EVALUATION_STANDARD_TO_AUDIT_STANDARD;
  public form: FormGroup<FormType>;

  @Input() config: SelectorConfig;

  @Input() evaluationMedium: EvaluationMediums = EvaluationMediums.web;
  @Output() standardsChange: EventEmitter<{
    evaluationStandards: EvaluationStandards[];
    conformanceLevel: standardNameAndLevel | null;
  }> = new EventEmitter();

  private formFieldErrors: Record<
    string,
    { fieldLabel: string; fieldError$: BehaviorSubject<string>; overrideErrors?: Record<string, string> }
  >;

  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private translateService: TranslateService,
    private formService: FormService,
    private formBuilder: FormBuilder,
  ) {
    this.form = this.formBuilder.group({
      [FormFields.evaluationStandards]: this.formBuilder.control<EvaluationStandards[]>([], [CustomValidators.required]),
      [FormFields.wcagStandard]: this.formBuilder.control<standardNameAndLevel>(null, [CustomValidators.required]),
    });
  }

  private initializeFormFieldErrors(): void {
    this.formFieldErrors = {
      [FormFields.evaluationStandards]: {
        fieldLabel: this.translateService.instant('accessibility_standard'),
        fieldError$: this.evaluationStandardsError$,
      },
      [FormFields.wcagStandard]: {
        fieldLabel: this.translateService.instant('wcag_standard'),
        fieldError$: this.wcagStandardsError$,
        overrideErrors: {
          required: this.translateService.instant('wcag_field_required_message'),
        },
      },
    };
  }

  private validateForm(): void {
    Object.keys(this.formFieldErrors).forEach((field: string) => {
      const { fieldLabel, fieldError$, overrideErrors } = this.formFieldErrors[field];
      fieldError$.next(this.formService.getErrorMessageForField(this.form.controls[field], fieldLabel, '', '', overrideErrors));
    });
  }

  public validate(): boolean {
    this.form.markAllAsTouched();
    this.validateForm();
    return this.form.valid;
  }

  protected getSelectedStandards(): EvaluationStandards[] {
    const nonWcagStandards: EvaluationStandards[] = this.form.value?.evaluationStandards?.filter(negate(isWcag)) ?? [];
    const wcagStandards: EvaluationStandards[] = SharedCommonUtility.notNullish(this.form.value?.wcagStandard)
      ? SharedAuditStandardsUtility.inflateWcagStandard(
          SharedAuditStandardsUtility.conformanceLevelToEvaluationStandard(this.form.value?.wcagStandard),
        )
      : [];

    return [...nonWcagStandards, ...wcagStandards];
  }

  public standardClicked(checked: boolean, standard: EvaluationStandards): void {
    const evaluationStandardsField: AbstractControl<any, any> = this.form.get(FormFields.evaluationStandards);
    const oldValue: EvaluationStandards[] = evaluationStandardsField.value ?? [];
    let newValue: EvaluationStandards[] = [...oldValue];
    if (checked) {
      newValue.push(standard);
    } else {
      newValue = newValue.filter((s: EvaluationStandards) => s !== standard);
    }
    evaluationStandardsField.patchValue(newValue);

    if (newValue.some(isWcag)) {
      this.form.get(FormFields.wcagStandard).setValidators([CustomValidators.required]);
    } else {
      this.form.get(FormFields.wcagStandard).clearValidators();
      this.form.get(FormFields.wcagStandard).setValue(null);
    }
  }

  public onSaveStandardsForm(): void {
    if (this.validate()) {
      const selectedStandards: EvaluationStandards[] = this.getSelectedStandards();
      const conformanceLevel: standardNameAndLevel = this.form.value.wcagStandard;
      if (!SharedCommonUtility.isNullishOrEmpty(selectedStandards)) {
        this.standardsChange.emit({ evaluationStandards: selectedStandards, conformanceLevel });
      }
    }
  }

  public getConformanceTranslationKey(standard: EvaluationStandards): string {
    if ([EvaluationStandards.en301549, EvaluationStandards.section508].includes(standard)) {
      return standard.toString();
    }
    return `${standard.toString()}_concise`;
  }

  public trackByStandard(_index: number, standard: EvaluationStandards): string {
    return String(standard);
  }

  public static createSelectorConfig(standards: IAuditStandard[]): SelectorConfig {
    const convertedStandards: EvaluationStandards[] = standards
      .map((standard: IAuditStandard) => IDENTIFIER_TO_EVALUATION_STANDARDS[standard[$auditStandard.identifier]])
      .filter((standard: EvaluationStandards) => !SharedCommonUtility.isStringEmpty(standard))
      .sort();
    const additionalWcagChoice: EvaluationStandards[] = convertedStandards.some(isWcag)
      ? [EvaluationStandards.WCAG_v20_A] // this is to show the "WCAG" option in the selector, it doesnt matter what WCAG level is set here
      : [];
    const selectorConfig: SelectorConfig = {
      mainStandardsChoice: convertedStandards
        .filter((standard: EvaluationStandards) => !isWcag(standard))
        .concat(additionalWcagChoice),
      wcagStandardConformanceLevels: convertedStandards.filter(isWcag),
    };
    return selectorConfig;
  }

  ngOnInit(): void {
    this.initializeFormFieldErrors();
  }

  get isWcagSelected(): boolean {
    return this.form.value?.evaluationStandards?.some((standard: EvaluationStandards) => isWcag(standard)) ?? false;
  }
}
